import { z } from "zod";

/** Role dentro da organização — alinhado com enum Role do Prisma. */
export const organizationRoleSchema = z.enum([
  "OWNER",
  /** Sócio: tudo que o proprietário pode, menos transferir a propriedade e mexer em outro sócio. */
  "PARTNER",
  "ADMIN",
  "DENTIST",
  "ASSISTANT",
  "RECEPTIONIST",
  "VIEWER",
]);

export type OrganizationRole = z.infer<typeof organizationRoleSchema>;

/** Role no nivel plataforma (cross-tenant). */
export const platformRoleSchema = z.enum(["MEMBER", "ADMIN"]);

export type PlatformRole = z.infer<typeof platformRoleSchema>;

/** Hierarquia pra ordenação/exibição — não é mais usada pra checar acesso a pacientes, agenda,
 *  arquivos, prótese, parceiros, equipe ou configurações (isso agora é @bookdental/schemas'
 *  DEFAULT_ROLE_PERMISSIONS + RolePermissionOverride, ver organization/permissions.ts). Só
 *  ownerProcedure (OWNER) segue sendo checado por rank aqui. */
const ROLE_RANK: Record<OrganizationRole, number> = {
  VIEWER: 0,
  RECEPTIONIST: 1,
  ASSISTANT: 2,
  DENTIST: 3,
  ADMIN: 4,
  PARTNER: 5,
  OWNER: 5,
};

/** Papéis com acesso total — o proprietário e o sócio. Nunca lidos da matriz de permissões. */
export const PAPEIS_COM_ACESSO_TOTAL = ["OWNER", "PARTNER"] as const;
export const ehDonoOuSocio = (role: string | null | undefined) => role === "OWNER" || role === "PARTNER";

/** Papéis que atendem paciente: recebem consulta e respondem por pedido de prótese. */
export const PAPEIS_DE_DENTISTA = ["OWNER", "PARTNER", "DENTIST"] as const;
export const ehPapelDeDentista = (role: string | null | undefined) =>
  (PAPEIS_DE_DENTISTA as readonly string[]).includes(role ?? "");

export function hasAtLeastRole(actual: OrganizationRole, required: OrganizationRole): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

/** Rótulos PT-BR — fonte única usada tanto no frontend (seletores de papel) quanto no backend
 *  (texto de notificações). Tipado `Record<string, string>` (não `Record<OrganizationRole, string>`)
 *  de propósito: várias telas indexam com um role vindo solto de query (`any`), e um mapped type
 *  sobre união literal quebraria essas leituras com TS7053. */
export const ROLE_LABEL: Record<string, string> = {
  OWNER: "Proprietário",
  PARTNER: "Sócio",
  ADMIN: "Administrador",
  DENTIST: "Dentista",
  ASSISTANT: "Assistente",
  RECEPTIONIST: "Recepção",
  VIEWER: "Visualizador",
};
